import { defineStore } from "pinia";
import { ref } from "vue";

import { apiClient } from "@/client";
import type {
	MapReviewQueryBody,
	MapReviewQueryEnvelope,
	MapReviewTreatmentRow,
	Treatment,
} from "@/type/api";

export const useStoreTreatment = defineStore("treatment", () => {
	// State
	const _byID = ref<Map<number, Treatment>>(new Map());
	const loading = ref(false);
	const error = ref<string | null>(null);

	// Actions
	/**
	 * One page of treatments through the generic QUERY endpoint
	 * (doc/in-progress/query.md §11 Phase 4): the same rows the ledger's
	 * Treatments table renders, with the envelope's `filtered_total` as the
	 * page's own total and `collection_total` as the district's.
	 */
	async function list(
		limit: number,
		offset: number,
	): Promise<MapReviewQueryEnvelope<MapReviewTreatmentRow>> {
		loading.value = true;
		error.value = null;
		try {
			const page = await apiClient.JSONQuery<
				MapReviewQueryEnvelope<MapReviewTreatmentRow>
			>("/api/treatment", { limit, offset } satisfies MapReviewQueryBody);
			// The list rows are the ledger's own projection, not the detail
			// shape (no resolved creator/product names), so they are not seeded
			// into the by-id cache: the detail page must fetch its own resolved
			// copy.
			return page;
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to load treatments";
			throw err;
		} finally {
			loading.value = false;
		}
	}

	async function fetchByID(id: number): Promise<Treatment> {
		const cached = _byID.value.get(id);
		if (cached) {
			return cached;
		}
		loading.value = true;
		error.value = null;
		try {
			const treatment = await apiClient.JSONGet<Treatment>(
				`/api/treatment/${id}`,
			);
			_byID.value.set(treatment.id, treatment);
			return treatment;
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to load treatment";
			throw err;
		} finally {
			loading.value = false;
		}
	}

	return {
		// Actions
		list,
		fetchByID,
		loading,
		error,
	};
});
